import { NavigateFunction } from 'react-router-dom';

import {
  BootResponse,
  DEFAULT_TRIGGERED_POPUP_BADGE_THUMBNAIL,
  EBadgePosition,
  OfferAPIResponse
} from '@appcharge/shared-ui';
import {
  EBadgeType,
  ImageDetails,
  Offer,
  OfferBadge,
  OfferUI,
  PopUpProductSequence,
  ProductsSequence,
  ProductsSequenceProduct,
  RollingOfferBadge,
  RollingOfferProductSequence
} from 'common/contracts';
import {
  EPopupSubType,
  ERewardCalendarPopUpTrigger,
  ESaleDiscountDisplayType,
  OfferType
} from 'constants/enums';
import { EToasterStatus, showToast } from 'utils/showToast';
import { REWARD_CALENDAR_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { DEFAULT_TEXT } from 'views/OffersUI/defaults';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import { z } from 'zod';

import { GradientDirection } from 'components/AcGradientInput/AcGraidentInput.types';
import { Product } from 'components/ProductsForm/ProductsForm.types';
import { bundleDesigns } from 'components/TemplatesStudio/DesignsMetadata/bundles.metadata';
import { ETemplateStudioProductPrioritiesFilter } from 'components/TemplatesStudio/enums';

import { promotionDesigns } from '../../../../DesignsMetadata/promotions.metadata';
import { templateStudioSchema } from '../../validation-schema';

const HEADER_BALANCES_LIMIT = 1;
const OFFER_PRODUCTS_LIMIT = 4;
const DEFAULT_AMOUNT_TO_SHOW = 100;
const DEFAULT_PRICE_IN_USD_CENTS = 990;
const DEFAULT_BADGE_COVER_IMAGE = DEFAULT_TRIGGERED_POPUP_BADGE_THUMBNAIL;
const DEFAULT_PROMOTION_TO_SHOW = [
  ETemplateStudioProductPrioritiesFilter.ALL_MAIN,
  ETemplateStudioProductPrioritiesFilter.MAIN_AND_SUBS,
  ETemplateStudioProductPrioritiesFilter.ALL_SUB
];
const DEFAULT_BUNDLE_TO_SHOW = [
  ETemplateStudioProductPrioritiesFilter.ALL_SUB,
  ETemplateStudioProductPrioritiesFilter.ALL_MAIN,
  ETemplateStudioProductPrioritiesFilter.MAIN_AND_SUBS,
  ETemplateStudioProductPrioritiesFilter.WEB_ONLY,
  ETemplateStudioProductPrioritiesFilter.ALL_MAIN,
  ETemplateStudioProductPrioritiesFilter.ALL_SUB
];

enum ProductType {
  SUB = 'Sub',
  MAIN = 'Main',
  WEB_ONLY = 'Web Only'
}

type BadgeWithType = OfferBadge | RollingOfferBadge;

interface ExtractedBadgeBase {
  name: string;
  publisherBadgeId: string;
  imageUrl: string;
  position?: EBadgePosition;
}

interface ExtractedRibbon extends ExtractedBadgeBase {
  text: string;
  backgroundColor?: {
    colorOne: string;
    colorTwo: string;
    gradientDirection: GradientDirection;
  };
  textColor?: {
    colorOne: string;
    colorTwo: string;
    gradientDirection: GradientDirection;
  };
}

type ExtractedBadge = ExtractedBadgeBase | ExtractedRibbon;

const extractBadges = (
  badges: BadgeWithType[],
  badgeType: EBadgeType.EMBLEM | EBadgeType.RIBBON,
  includePosition: boolean = true
): ExtractedBadge[] => {
  const defaultPosition =
    badgeType === EBadgeType.EMBLEM
      ? EBadgePosition.LEFT
      : EBadgePosition.CENTER;

  return badges
    .filter((b) => b.badge?.type === badgeType)
    .map((b) => {
      const base = {
        name: b.badge?.name || '',
        publisherBadgeId: b.badge?.publisherBadgeId || '',
        imageUrl:
          badgeType === EBadgeType.EMBLEM
            ? b.badge?.emblem?.imageUrl || ''
            : b.badge?.ribbon?.imageUrl || ''
      };

      if (badgeType === EBadgeType.RIBBON) {
        const position =
          b.badge?.position ||
          ('position' in b ? b.position : undefined) ||
          defaultPosition;
        const ribbonBg = b.badge?.ribbon?.backgroundColor;
        const ribbonTextColor = b.badge?.ribbon?.textColor;
        return {
          ...base,
          text: b.badge?.ribbon?.text || '',
          position: includePosition ? position : undefined,
          backgroundColor: {
            colorOne: ribbonBg?.colorOne || '',
            colorTwo: ribbonBg?.colorTwo || '',
            gradientDirection:
              ribbonBg?.gradientDirection || GradientDirection.TopToBottom
          },
          textColor: {
            colorOne: ribbonTextColor?.colorOne || '',
            colorTwo: ribbonTextColor?.colorTwo || '',
            gradientDirection:
              ribbonTextColor?.gradientDirection ||
              GradientDirection.TopToBottom
          }
        };
      }

      // Emblem case
      const position =
        b.badge?.position ||
        ('position' in b ? b.position : undefined) ||
        defaultPosition;
      return {
        ...base,
        position: includePosition ? position : undefined
      };
    });
};

const createOffer = (offer: Offer, offerUIS: OfferUI[], boot: BootResponse) => {
  const offerUI = offerUIS.find((ui) => ui.externalId === offer.offerUiId);
  const offerType = offer.type;
  if (!offerUI) {
    return;
  }

  const allSequenceBadges: RollingOfferBadge[] = [];
  offer.productsSequence?.forEach((sequence) => {
    if ('badges' in sequence && sequence.badges) {
      allSequenceBadges.push(...sequence.badges);
    }
  });

  const offerBadges = offer.badges || [];
  const offerLevelEmblems = extractBadges(offerBadges, EBadgeType.EMBLEM);
  const offerLevelRibbons = extractBadges(offerBadges, EBadgeType.RIBBON);

  const sequenceEmblems = extractBadges(allSequenceBadges, EBadgeType.EMBLEM);
  const sequenceRibbons = extractBadges(allSequenceBadges, EBadgeType.RIBBON);

  const combinedEmblems =
    offerLevelEmblems.length > 0 ? offerLevelEmblems : sequenceEmblems;
  const combinedRibbons =
    offerLevelRibbons.length > 0 ? offerLevelRibbons : sequenceRibbons;

  const offerBadgeCoverImage =
    offer.offerUi?.badgeCoverImage || DEFAULT_BADGE_COVER_IMAGE;

  const offerLevelSubType = (offer as Offer & { subType?: string }).subType;

  const mergedOfferUI = {
    ...offerUI,
    badgeCoverImage: offerBadgeCoverImage,
    ...(offerLevelSubType && { offerUiSubType: offerLevelSubType })
  };

  const offerSubType =
    offerLevelSubType ??
    (mergedOfferUI as { offerUiSubType?: string }).offerUiSubType;

  const baseOffer = {
    offerId: offer.offerId,
    publisherOfferId: offer.publisherOfferId,
    offerJwt: (offer as Offer & { offerJwt?: string }).offerJwt || '',
    offerType: offer.type,
    ...(offerSubType != null && { subType: offerSubType }),
    offerName: offer.name,
    displayName: offer.displayName,
    offerUi: mergedOfferUI,
    isAllowedToCollect: offer.isAllowedToCollect,
    ...(offer.rewardCalendar != null && { rewardCalendar: offer.rewardCalendar }),
    dynamicOfferUi: {
      badges: offer.badges || [],
      salePercentage: offer.productSale?.sale || 0,
      salePercentageDisplayType:
        offer.productSale?.type === 'percentage' ? 'percentage' : 'value',
      offerBadges: {
        emblems: {
          data: combinedEmblems
        },
        ribbons: {
          data: combinedRibbons
        }
      }
    },
    badges: offer.badges || [],
    productsSequence:
      offer.productsSequence?.map((sequence) => {
        const isRollingSequence = isRollingOfferSequence(sequence);
        const hasSequenceBadges =
          'badges' in sequence && sequence.badges && sequence.badges.length > 0;
        const hasDiscount =
          (isRollingSequence &&
            sequence.priceDiscount?.discount &&
            sequence.priceDiscount.discount > 0) ||
          (offer.productSale?.sale && offer.productSale?.sale > 0);
        const originalPrice =
          hasDiscount && isRollingSequence && sequence.priceDiscount
            ? sequence.priceDiscount.type === 'percentage'
              ? sequence.priceInUsdCents /
                (1 - (sequence.priceDiscount.discount || 0) / 100)
              : sequence.priceInUsdCents +
                (sequence.priceDiscount.discount || 0)
            : offer.productSale?.type === 'percentage'
              ? sequence.priceInUsdCents /
                (1 - (offer.productSale?.sale || 0) / 100)
              : sequence.priceInUsdCents + (offer.productSale?.sale || 0);

        const sequenceResult = {
          index: sequence.index,
          products: sequence.products.map((prod, index) => {
            const isFirstProduct = index === 0;
            const product =
              typeof prod.product === 'string' ? null : prod.product;

            const productImages =
              product && 'images' in product ? product.images : undefined;
            const productImage =
              productImages?.find(
                (img: ImageDetails) => img.type === 'product'
              ) || productImages?.[0];
            const productPrefix =
              productImages?.find(
                (img: ImageDetails) => img.type === 'productPrefix'
              ) || productImages?.[1];

            const prefix = product?.prefix || '';
            const suffix = product?.suffix || '';
            const quantity = Number(prod?.quantity) || 0;
            const quantityDisplay =
              prefix || suffix
                ? `${prefix} ${quantity} ${suffix}`.trim()
                : `${quantity}`;

            const productPriority =
              'priority' in prod ? prod.priority : product?.priority;

            const prodWithExtras = prod as ProductsSequenceProduct & {
              traits?: unknown[];
            };
            const traits =
              prodWithExtras.traits ??
              (product && 'traits' in product
                ? (product as { traits?: unknown[] }).traits
                : undefined);
            const rarityProductInfo =
              prodWithExtras.rarityProductInfo ??
              (product && 'rarityProductInfo' in product
                ? (product as { rarityProductInfo?: unknown }).rarityProductInfo
                : undefined);
            const badgeText =
              prodWithExtras.badgeText ??
              (product && 'badgeText' in product
                ? (product as { badgeText?: string }).badgeText
                : undefined);
            return {
              publisherProductId: product?.publisherProductId,
              images: {
                product: productImage?.url || '',
                productPrefix: productPrefix?.url || ''
              },
              name: product?.name,
              description:
                product && 'description' in product
                  ? product.description || ''
                  : '',
              textFontColorHex: product?.textFontColorHex,
              priority: productPriority || 'Main',
              prefix: product?.prefix || '',
              suffix: product?.suffix || '',
              type: product?.type,
              quantity: quantity,
              quantityDisplay: quantityDisplay,
              strokedSaleQuantity:
                (isRollingSequence && sequence?.productSale?.sale) ||
                (isFirstProduct && offer.productSale?.sale)
                  ? `${quantity}`
                  : '',
              displayName:
                product && 'displayName' in product
                  ? product.displayName || product.name || ''
                  : product?.name || '',
              traits: Array.isArray(traits) ? traits : [],
              ...(rarityProductInfo != null && { rarityProductInfo }),
              ...(badgeText != null && badgeText !== '' && { badgeText })
            };
          }),
          price: {
            currencyCode: 'USD',
            currencySymbol: '$',
            price: sequence.priceInUsdCents || 0,
            symbolPosition: 'Left',
            spacing: false,
            fractionalSeparator:
              boot.storeTheme?.general?.productsQuantityFormat
                ?.fractionalSeparator || '.',
            milSeparator:
              boot.storeTheme?.general?.productsQuantityFormat?.milSeparator ||
              ',',
            isCents: true,
            multiplier: 100
          },
          priceInUsdCents: sequence.priceInUsdCents || 0,
          prices: {
            priceInUsdCents: sequence.priceInUsdCents || 0,
            priceDisplay: `$${((sequence.priceInUsdCents || 0) / 100).toFixed(2)}`,
            ...(hasDiscount && {
              strokedPriceDisplay: `$${(originalPrice / 100).toFixed(2)}`
            }),
            currencyCode: 'USD'
          },
          ...(isRollingSequence &&
            sequence.priceDiscount && {
              priceDiscount: {
                discount: sequence.priceDiscount.discount,
                type: sequence.priceDiscount.type
              }
            }),
          ...(isRollingSequence &&
            sequence.productSale && {
              productSale: {
                sale: sequence.productSale.sale,
                type: sequence.productSale.type
              }
            }),
          ...(hasSequenceBadges && {
            badges: sequence.badges
          }),
          offerBadges: {
            emblems:
              hasSequenceBadges && sequence.badges
                ? extractBadges(sequence.badges, EBadgeType.EMBLEM, false)
                : [],
            ribbons:
              hasSequenceBadges && sequence.badges
                ? extractBadges(sequence.badges, EBadgeType.RIBBON, false)
                : []
          },
          displayName:
            isRollingSequence && sequence.displayName
              ? sequence.displayName
              : offer.displayName
        };

        return sequenceResult;
      }) || [],
    schedule: offer.schedule
  };

  if (offerType === OfferType.ROLLING_OFFER) {
    return {
      ...baseOffer,
      priority: (offer as Offer & { priority?: number }).priority || 1,
      indexToCollect:
        (offer as Offer & { indexToCollect?: number }).indexToCollect || 1
    };
  }

  return {
    ...baseOffer,
    productSale: {
      sale: offer.productSale?.sale,
      type: offer.productSale?.type
    },
    priceDiscount: {
      discount: offer.priceDiscount?.discount,
      type: offer.priceDiscount?.type
    },
    playerAvailability: {
      total: offer.playerAvailability || 2,
      remaining: offer.playerAvailability || 2
    },
    offerBadges: {
      emblems: combinedEmblems,
      ribbons: combinedRibbons
    },
    endsIn:
      (offer as Offer & { endsIn?: number }).endsIn ??
      Date.now() + 3 * 24 * 60 * 60 * 1000
  };
};

const isRollingOfferSequence = (
  sequence:
    | ProductsSequence
    | PopUpProductSequence
    | RollingOfferProductSequence
): sequence is RollingOfferProductSequence => {
  return (
    'priceDiscount' in sequence ||
    'productSale' in sequence ||
    'badges' in sequence ||
    'displayName' in sequence
  );
};

const generateProduct = (
  product: Product,
  priority: ProductType,
  quantity = 200
): ProductsSequenceProduct => {
  return {
    product: {
      ...product,
      productId: product?.productId || '',
      _id: product?.productId || '',
      image: product?.images?.[0]?.url || '',
      imagePrefix: product?.images?.[1]?.url || '',
      amount: DEFAULT_AMOUNT_TO_SHOW,
      quantityDisplay: DEFAULT_AMOUNT_TO_SHOW.toString()
    },
    quantity: quantity,
    priority,
    publisherProductId: product?.publisherProductId,
    textFontColorHex: ''
  };
};

const generateProductsAllSubs = (
  products: Product[]
): ProductsSequenceProduct[] => {
  return [
    ...products.map((product) => generateProduct(product, ProductType.SUB))
  ];
};

const generateAllMainProducts = (
  products: Product[]
): ProductsSequenceProduct[] => {
  return [
    ...products.map((product) => generateProduct(product, ProductType.MAIN))
  ];
};

const generateMainAndSubsProducts = (
  products: Product[]
): ProductsSequenceProduct[] => {
  return [
    generateProduct(products[0], ProductType.MAIN),
    ...(products.length > 1 ? products.slice(1) : products).map((product) =>
      generateProduct(product, ProductType.SUB)
    )
  ];
};

const generateWebonlyProducts = (
  products: Product[]
): ProductsSequenceProduct[] => {
  const hey = [
    ...products.map((product) =>
      generateProduct(product, ProductType.WEB_ONLY, 200000)
    )
  ];
  return hey;
};

const generateProductsByPriority = (
  priority: ETemplateStudioProductPrioritiesFilter,
  products: Product[]
): ProductsSequenceProduct[] => {
  switch (priority) {
    case ETemplateStudioProductPrioritiesFilter.ALL_SUB:
      return generateProductsAllSubs(products);
    case ETemplateStudioProductPrioritiesFilter.ALL_MAIN:
      return generateAllMainProducts(products);
    case ETemplateStudioProductPrioritiesFilter.MAIN_AND_SUBS:
      return generateMainAndSubsProducts(products);
    case ETemplateStudioProductPrioritiesFilter.WEB_ONLY:
      return [
        ...generateWebonlyProducts(products),
        ...generateProductsAllSubs(products)
      ];
    default:
      return [];
  }
};

const generateProductSequenceByPriority = (
  index: number,
  priority: ETemplateStudioProductPrioritiesFilter,
  products: Product[]
): ProductsSequence[] => {
  return [
    {
      index,
      products: generateProductsByPriority(priority, products),
      priceInUsdCents: DEFAULT_PRICE_IN_USD_CENTS
    }
  ];
};

const generateDefaultOffer = (
  type: OfferType,
  externalOfferId: string
): Offer => {
  return {
    publisherOfferId: '',
    name: '',
    type,
    offerUiId: externalOfferId,
    offerUi: {
      offerUiId: '',
      active: true,
      offerUiType: type,
      name: '',
      description: '',
      backgroundImage: '',
      specialOffer: {
        templateType: DEFAULT_TEXT.single,
        presentOfferEndTimer: false,
        title: '',
        fontSize: 16,
        fontWeight: 'normal',
        fontColor: {
          colorOne: '#000000',
          colorTwo: '#000000',
          direction: 'to bottom'
        },
        subTitle: {
          text: '',
          fontSize: 16,
          fontWeight: 'normal',
          fontColor: {
            colorOne: '#000000',
            colorTwo: '#000000',
            direction: 'to bottom'
          }
        }
      },
      progressBar: {
        barFillColor: {
          colorOne: '#FFD450',
          colorTwo: '#EDB923',
          gradientDirection: 'to bottom'
        },
        barRadius: 12,
        accumulatedItem: {
          imageUrl:
            'https://media.appcharge.com/defaults/template-studio/progress-bar/accumulated.png',
          backgroundColor: {
            colorOne: '#F28CFE',
            colorTwo: '#9539FB',
            gradientDirection: 'to bottom'
          },
          backgroundImageUrl: ''
        },
        backgroundOpacity: 60,
        frameRadius: 12,
        backgroundColor: {
          colorOne: '#DABA98',
          colorTwo: '#FDFCF9',
          gradientDirection: 'to right'
        }
      }
    },
    active: true,
    coolDownInHours: 1,
    segments: [],
    productsSequence: [],
    createdBy: 'API',
    triggers: []
  };
};

const ROLLING_OFFER_DEFAULT_DISCOUNT_PERCENTAGE = 12;

const buildRollingSequence = (
  products: Product[],
  paidTileIndex: number,
  hasDiscount: boolean
) => {
  return products.map((product, sequenceIndex) => {
    const isPaidTile = sequenceIndex === paidTileIndex;
    const priceInUsdCents = isPaidTile ? DEFAULT_PRICE_IN_USD_CENTS : 0;

    const originalPrice =
      hasDiscount && priceInUsdCents > 0
        ? priceInUsdCents /
          (1 - ROLLING_OFFER_DEFAULT_DISCOUNT_PERCENTAGE / 100)
        : priceInUsdCents;

    return {
      index: sequenceIndex + 1,
      products: [
        {
          product: {
            ...product,
            productId: product.productId || '',
            images: product.images || [],
            amount: DEFAULT_AMOUNT_TO_SHOW,
            quantityDisplay: DEFAULT_AMOUNT_TO_SHOW.toString()
          },
          quantity: 200,
          publisherProductId: product.publisherProductId || ''
        }
      ],
      price: {
        currencyCode: 'USD',
        currencySymbol: '$',
        price: priceInUsdCents,
        symbolPosition: 'Left',
        spacing: false,
        fractionalSeparator: '.',
        milSeparator: ',',
        isCents: true,
        multiplier: 100
      },
      priceInUsdCents,
      prices: {
        priceInUsdCents,
        priceDisplay: `$${(priceInUsdCents / 100).toFixed(2)}`,
        ...(hasDiscount &&
          priceInUsdCents > 0 && {
            strokedPriceDisplay: `$${(originalPrice / 100).toFixed(2)}`
          }),
        currencyCode: 'USD'
      },
      ...(hasDiscount &&
        priceInUsdCents > 0 && {
          priceDiscount: {
            discount: ROLLING_OFFER_DEFAULT_DISCOUNT_PERCENTAGE,
            type: 'percentage' as const
          }
        }),
      badges: [],
      offerBadges: {
        emblems: [],
        ribbons: []
      },
      displayName: 'Rolling Offer'
    };
  });
};

const STANDARD_ROLLING_PREVIEW_PAID_TILE_INDEX = 2;

const generateStandardRollingOffer = (
  products: Product[],
  externalOfferUIId: string,
  priority: number
): Offer | null => {
  if (!products.length) return null;
  const sequence = buildRollingSequence(
    products,
    STANDARD_ROLLING_PREVIEW_PAID_TILE_INDEX,
    true
  );
  return {
    ...generateDefaultOffer(OfferType.ROLLING_OFFER, externalOfferUIId),
    displayName: 'Rolling Offer',
    priority,
    indexToCollect: 1,
    productsSequence: sequence
  } as Offer & { priority: number; indexToCollect: number };
};

const findRollingOfferDesignExternalId = (
  offerUIS: OfferUI[],
  designId: string
): string => {
  if (!designId) return '';
  const match = offerUIS.find(
    (ui) =>
      ui.offerUiType === OfferDesignTypes.RollingOffer &&
      (ui._id === designId || ui.externalId === designId)
  );
  return match?.externalId ?? '';
};

const generateRollingOffers = (
  formValues: z.infer<typeof templateStudioSchema>,
  products: Product[],
  offerUIS: OfferUI[]
): Offer[] => {
  const offers: Offer[] = [];

  const standardExternalId =
    findRollingOfferDesignExternalId(
      offerUIS,
      formValues?.rollingOffers?.selectedDesignId ?? ''
    ) ||
    offerUIS.find((ui) => ui.offerUiType === OfferDesignTypes.RollingOffer)
      ?.externalId ||
    '';

  const standardOffer = generateStandardRollingOffer(
    products,
    standardExternalId,
    1
  );
  if (standardOffer) {
    offers.push(standardOffer);
  }

  return offers;
};

const generateProgressBars = (
  _: z.infer<typeof templateStudioSchema>,
  products: Product[],
  offerUIS: OfferUI[]
): Offer[] => {
  const externalOfferUIId =
    offerUIS.find((ui) => ui.offerUiType === OfferDesignTypes.ProgressBar)
      ?.externalId ?? '';

  // const progressBarSequences = products.map((product, index) => {
  //   // Different pricing for each sequence
  //   const basePrice = DEFAULT_PRICE_IN_USD_CENTS; // Vary prices: 30, 80, 130...
  //   const priceInUsdCents = index === 2 ? basePrice : 0; // third one is priced

  //   // Calculate original price before discount
  //   const originalPrice =
  //     hasDiscount && priceInUsdCents > 0
  //       ? priceInUsdCents / (1 - discountPercentage / 100)
  //       : priceInUsdCents;

  //   return {
  //     index: index + 1,
  //     products: [
  //       {
  //         product: {
  //           ...product,
  //           productId: product.productId || '',
  //           images: product.images || [], // Keep original images array structure
  //           amount: DEFAULT_AMOUNT_TO_SHOW,
  //           quantityDisplay: DEFAULT_AMOUNT_TO_SHOW.toString()
  //         },
  //         quantity: 200,
  //         publisherProductId: product.publisherProductId || ''
  //       }
  //     ],
  //     price: {
  //       currencyCode: 'USD',
  //       currencySymbol: '$',
  //       price: priceInUsdCents,
  //       symbolPosition: 'Left',
  //       spacing: false,
  //       fractionalSeparator: '.',
  //       milSeparator: ',',
  //       isCents: true,
  //       multiplier: 100
  //     },
  //     priceInUsdCents: priceInUsdCents,
  //     prices: {
  //       priceInUsdCents: priceInUsdCents,
  //       priceDisplay: `$${(priceInUsdCents / 100).toFixed(2)}`,
  //       ...(hasDiscount &&
  //         priceInUsdCents > 0 && {
  //           strokedPriceDisplay: `$${(originalPrice / 100).toFixed(2)}`
  //         }),
  //       currencyCode: 'USD'
  //     },
  //     ...(hasDiscount &&
  //       priceInUsdCents > 0 && {
  //         priceDiscount: {
  //           discount: discountPercentage,
  //           type: 'percentage' as const
  //         }
  //       }),
  //     // ...(hasDiscount &&
  //     //   priceInUsdCents > 0 && {
  //     //     productSale: {
  //     //       sale: salePercentage,
  //     //       type: 'percentage' as const
  //     //     }
  //     //   }),
  //     badges: [],
  //     offerBadges: {
  //       emblems: [],
  //       ribbons: []
  //     },
  //     displayName: 'Rolling Offer'
  //   };
  // });

  // todo
  return [
    {
      ...generateDefaultOffer(OfferType.PROGRESS_BAR, externalOfferUIId),
      displayName: 'Progress Bar',
      priority: 1,
      activeMissionIndex: 2,
      badges: [],
      collectedPoints: 100,
      productsSequence: [
        {
          index: 1,
          products: [
            {
              publisherProductId: 'Product_A432',
              images: {
                product:
                  'https://media.appcharge.com/defaults/template-studio/progress-bar/product.png'
              },
              name: 'Product_A432',
              textFontColorHex: '#c3328e',
              priority: 'Sub',
              prefix: '',
              suffix: '',
              quantity: 50,
              quantityDisplay: '50',
              strokedSaleQuantity: '',
              displayName: 'Product_A432'
            }
          ],
          priceInUsdCents: 0,
          barSize: 100
        },
        {
          index: 2,
          products: [
            {
              publisherProductId: 'Product_C',
              images: {
                product:
                  'https://media.appcharge.com/defaults/template-studio/progress-bar/product.png',
                productPrefix: ''
              },
              name: 'Product_C',
              textFontColorHex: '#FFFFFF',
              priority: 'Sub',
              prefix: '',
              suffix: '',
              quantity: 2000,
              quantityDisplay: '2,000',
              strokedSaleQuantity: '',
              displayName: 'Product_C'
            }
          ],
          priceInUsdCents: 0,
          barSize: 200
        },
        {
          index: 3,
          products: [
            {
              publisherProductId: 'Product_B',
              images: {
                product:
                  'https://media.appcharge.com/defaults/template-studio/progress-bar/product.png',
                productPrefix: ''
              },
              name: 'Product_B',
              textFontColorHex: '#ffffff',
              priority: 'Sub',
              prefix: '',
              suffix: '',
              quantity: 250,
              quantityDisplay: '250',
              strokedSaleQuantity: '',
              displayName: 'Product_B'
            }
          ],
          priceInUsdCents: 0,
          barSize: 400
        }
      ]
    }
  ];
};

const generateBalances = (products: Product[]) => {
  return products.slice(0, HEADER_BALANCES_LIMIT).map((product, index) => {
    const baseQuantity = 100000;
    const formattedAmount = baseQuantity.toLocaleString();

    const productImage =
      product.images?.find((img) => img.type === 'product') ||
      product.images?.[0];
    const productPrefixImage =
      product.images?.find((img) => img.type === 'productPrefix') ||
      product.images?.[1];

    return {
      product: {
        _id: {},
        publisherId: {},
        publisherProductId:
          product.publisherProductId || `${product.productId || index}`,
        name: product.name || `Product ${index + 1}`,
        textFontColorHex: product.textFontColorHex || '#FFFFFF',
        type: product.type || 'Quantity',
        prefix: product.prefix || '',
        suffix: product.suffix || '',
        priority: product.priority || 'Main',
        images: product.images || [],
        displayName:
          product.displayName || product.name || `Product ${index + 1}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        __v: 0
      },
      quantity: String(baseQuantity),
      formattedAmount: formattedAmount,
      images: {
        product: productImage?.url || '',
        productPrefix: productPrefixImage?.url || ''
      }
    };
  });
};

const generatePromotionOffers = (
  formValues: z.infer<typeof templateStudioSchema>,
  products: Product[],
  offerUIS: OfferUI[],
  promotionBadges: OfferBadge[] = []
): Offer[] => {
  const designId = formValues.promotion.selectedDesignId;
  const designMetadata = Object.values(promotionDesigns).find(
    ({ id }) => id === designId
  );

  if (!designMetadata) {
    console.error('no design metadata that matches the design id:', designId);
    return [];
  }

  const externalOfferUIId =
    offerUIS.find((ui) => ui.offerUiType === OfferDesignTypes.SpecialOffer)
      ?.externalId ?? '';

  const defaultEmblemBadge: OfferBadge = {
    badgeId: '68c94aff34d93f62e75406ca',
    publisherBadgeId: '1111',
    position: EBadgePosition.LEFT,
    badge: {
      name: 'Default Emblem Badge',
      publisherBadgeId: '1111',
      type: EBadgeType.EMBLEM,
      position: EBadgePosition.LEFT,
      emblem: {
        imageUrl:
          'https://media.appcharge.com/defaults/template-studio/promotions/emblem_badge_with_shadow.png'
      },
      badgeId: '68c94aff34d93f62e75406ca'
    }
  };

  const defaultRibbonBadge: OfferBadge = {
    badgeId: '68c94aff34d93f62e75406cb',
    publisherBadgeId: '2222',
    position: EBadgePosition.TOP_RIGHT,
    badge: {
      name: 'Default Ribbon Badge',
      publisherBadgeId: '2222',
      type: EBadgeType.RIBBON,
      position: EBadgePosition.TOP_RIGHT,
      ribbon: {
        text: '+100%',
        backgroundColor: {
          colorOne: '#3AB6FA',
          colorTwo: '#006DAA',
          gradientDirection: GradientDirection.TopToBottom
        },
        textColor: {
          colorOne: '#ffffff',
          colorTwo: '',
          gradientDirection: GradientDirection.TopToBottom
        }
      },
      badgeId: '68c94aff34d93f62e75406cb'
    }
  };

  const defaultProductSale = {
    sale: 50,
    type: ESaleDiscountDisplayType.PERCENTAGE
  };

  const offersToReturn = DEFAULT_PROMOTION_TO_SHOW.map((priority, index) => {
    const isFirstOffer = index === 0;

    const productsSequence = generateProductSequenceByPriority(
      index,
      priority,
      products
    );

    if (isFirstOffer && productsSequence[0]?.products[0]) {
      productsSequence[0].products[0].strokedSaleQuantity = '100';
    }

    return {
      ...generateDefaultOffer(OfferType.SPECIAL_OFFER, externalOfferUIId),
      productsSequence: productsSequence,
      badges: promotionBadges,
      endsIn: 259200000,
      ...(isFirstOffer && { productSale: defaultProductSale })
    };
  });

  if (!promotionBadges || promotionBadges.length === 0) {
    offersToReturn[1].badges = [defaultRibbonBadge];
    offersToReturn[2].badges = [defaultEmblemBadge];
  }

  return offersToReturn;
};

const createDefaultPopupOfferUI = (): OfferUI => ({
  _id: '',
  active: true,
  offerUiType: OfferDesignTypes.PopUp,
  offerUiSubType: EPopupSubType.DAILY_BONUS,
  name: 'Default Daily Bonus Popup',
  description: '',
  backgroundImage: '',
  badgeCoverImage: DEFAULT_BADGE_COVER_IMAGE,
  externalId: 'default-daily-bonus-popup',
  popup: {
    templateType: DEFAULT_TEXT.single
  },
  borderWidth: 2,
  borderColor: {
    colorOne: '#ffffff',
    colorTwo: '',
    direction: 'to bottom'
  }
});

const createDefaultStoreRefreshPopupOfferUI = (): OfferUI => ({
  _id: '',
  active: true,
  offerUiType: OfferDesignTypes.PopUp,
  offerUiSubType: EPopupSubType.STORE_REFRESH,
  name: 'Default Store Refresh Popup',
  description: '',
  backgroundImage: '',
  badgeCoverImage: DEFAULT_BADGE_COVER_IMAGE,
  externalId: 'default-store-refresh-popup',
  popup: {
    templateType: DEFAULT_TEXT.single
  },
  borderWidth: 2,
  borderColor: {
    colorOne: '#ffffff',
    colorTwo: '',
    direction: 'to bottom'
  }
});

const createDefaultRewardCalendarPopupOfferUI = (): OfferUI => ({
  _id: '',
  active: true,
  offerUiType: OfferDesignTypes.PopUp,
  offerUiSubType: EPopupSubType.REWARD_CALENDAR,
  name: 'Default Reward Calendar Popup',
  description: '',
  backgroundImage: '',
  externalId: 'default-reward-calendar-popup',
  popup: {
    templateType: DEFAULT_TEXT.single
  },
  borderWidth: 2,
  borderColor: {
    colorOne: '#ffffff',
    colorTwo: '',
    direction: 'to bottom'
  },
  rewardCalendar: {
    backgroundColor: REWARD_CALENDAR_DEFAULTS.backgroundColor,
    dayFontColor: REWARD_CALENDAR_DEFAULTS.dayFontColor,
    collectibleBadgeImage: REWARD_CALENDAR_DEFAULTS.collectibleBadgeImage,
    lockedBadgeImage: REWARD_CALENDAR_DEFAULTS.lockedBadgeImage,
    collectIconImage: REWARD_CALENDAR_DEFAULTS.collectIconImage,
    lockedIconImage: REWARD_CALENDAR_DEFAULTS.lockedIconImage
  }
});

const generatePopupsOffers = (
  products: Product[],
  offerUIS: OfferUI[]
): Offer[] => {
  const dailyBonusPopupOfferUI = offerUIS.find(
    (ui) =>
      ui.offerUiType === OfferDesignTypes.PopUp &&
      ui.offerUiSubType === EPopupSubType.DAILY_BONUS
  );

  const finalPopupOfferUI =
    dailyBonusPopupOfferUI || createDefaultPopupOfferUI();
  const externalOfferUIId = finalPopupOfferUI.externalId;

  const badgeCoverImage =
    finalPopupOfferUI.badgeCoverImage || DEFAULT_BADGE_COVER_IMAGE;

  // Generate a single popup offer with all available product priorities
  const popupPriorities = [
    ETemplateStudioProductPrioritiesFilter.ALL_SUB,
    ETemplateStudioProductPrioritiesFilter.ALL_MAIN,
    ETemplateStudioProductPrioritiesFilter.MAIN_AND_SUBS,
    ETemplateStudioProductPrioritiesFilter.WEB_ONLY
  ];

  const dailyBonusPopups = popupPriorities.map((priority, index) => {
    const defaultOffer = generateDefaultOffer(
      OfferType.POPUP,
      externalOfferUIId
    );
    return {
      ...defaultOffer,
      offerId: `preview-daily-bonus-${index}`,
      productsSequence: generateProductSequenceByPriority(
        index,
        priority,
        products
      ),
      offerUi: {
        ...defaultOffer.offerUi,
        badgeCoverImage
      } as typeof defaultOffer.offerUi & { badgeCoverImage?: string }
    };
  });

  const storeRefreshPopupOfferUI =
    offerUIS.find(
      (ui) =>
        ui.offerUiType === OfferDesignTypes.PopUp &&
        (ui.offerUiSubType === EPopupSubType.STORE_REFRESH ||
          ui.offerUiSubType === EPopupSubType.POST_PURCHASE)
    ) || createDefaultStoreRefreshPopupOfferUI();

  const storeRefreshBadgeCoverImage =
    storeRefreshPopupOfferUI.badgeCoverImage || DEFAULT_BADGE_COVER_IMAGE;

  const storeRefreshPopup = (() => {
    const defaultOffer = generateDefaultOffer(
      OfferType.POPUP,
      storeRefreshPopupOfferUI.externalId
    );
    return {
      ...defaultOffer,
      offerId: 'preview-store-refresh',
      subType: EPopupSubType.STORE_REFRESH,
      productsSequence: generateProductSequenceByPriority(
        0,
        ETemplateStudioProductPrioritiesFilter.MAIN_AND_SUBS,
        products
      ),
      offerUi: {
        ...defaultOffer.offerUi,
        ...storeRefreshPopupOfferUI,
        offerUiSubType: EPopupSubType.STORE_REFRESH,
        badgeCoverImage: storeRefreshBadgeCoverImage
      } as typeof defaultOffer.offerUi & { badgeCoverImage?: string }
    } as Offer & { subType: string };
  })();

  const rewardCalendarPopupOfferUI =
    offerUIS.find(
      (ui) =>
        ui.offerUiType === OfferDesignTypes.PopUp &&
        ui.offerUiSubType === EPopupSubType.REWARD_CALENDAR
    ) || createDefaultRewardCalendarPopupOfferUI();

  const rewardCalendarDaysSequence: ProductsSequence[] = Array.from(
    { length: 7 },
    (_, i) => {
      const product = products.length > 0 ? products[i % products.length] : undefined;
      return {
        index: i + 1,
        products: product ? [generateProduct(product, ProductType.SUB)] : [],
        priceInUsdCents: 0
      };
    }
  );

  const buildRewardCalendarPopup = (
    suffix: string,
    isAllowedToCollect: boolean
  ): Offer & { subType: string } => {
    const defaultOffer = generateDefaultOffer(
      OfferType.POPUP,
      rewardCalendarPopupOfferUI.externalId
    );
    return {
      ...defaultOffer,
      offerId: `preview-reward-calendar-${suffix}`,
      subType: EPopupSubType.REWARD_CALENDAR,
      productsSequence: rewardCalendarDaysSequence,
      isAllowedToCollect,
      rewardCalendar: {
        popupTrigger: ERewardCalendarPopUpTrigger.SHOW_AUTOMATICALLY
      },
      offerUi: {
        ...defaultOffer.offerUi,
        ...rewardCalendarPopupOfferUI,
        offerUiSubType: EPopupSubType.REWARD_CALENDAR
      } as typeof defaultOffer.offerUi
    } as Offer & { subType: string };
  };

  const rewardCalendarCollectiblePopup = buildRewardCalendarPopup(
    'collectible',
    true
  );
  const rewardCalendarLockedPopup = buildRewardCalendarPopup('locked', false);

  return [
    ...dailyBonusPopups,
    storeRefreshPopup,
    rewardCalendarCollectiblePopup,
    rewardCalendarLockedPopup
  ];
};

const generateBundles = (
  formValues: z.infer<typeof templateStudioSchema>,
  products: Product[],
  offerUIS: OfferUI[]
): Offer[] => {
  const defaultProductSale = {
    sale: 50,
    type: ESaleDiscountDisplayType.PERCENTAGE
  };

  const designId = formValues.bundle.selectedDesignId;
  const designMetadata = Object.values(bundleDesigns).find(
    ({ id }) => id === designId
  );

  const externalOfferUIId =
    offerUIS.find((ui) => ui.offerUiType === OfferDesignTypes.Bundle)
      ?.externalId ?? '';
  if (designMetadata) {
    const bundlesToReturn = DEFAULT_BUNDLE_TO_SHOW.map((priority, index) => {
      const isFirstOffer = index === 0;

      return {
        ...generateDefaultOffer(OfferType.BUNDLE, externalOfferUIId),
        productsSequence: generateProductSequenceByPriority(
          index,
          priority,
          products
        ),
        ...(isFirstOffer && { productSale: defaultProductSale })
      };
    });

    return bundlesToReturn;
  }

  return [];
};

export const generateOffersAPIResponse = ({
  formValues,
  boot,
  products,
  maxBundlePreviewProducts,
  offerUIS,
  promotionBadges = [],
  tabs = [],
  navigate
}: {
  formValues: z.infer<typeof templateStudioSchema>;
  boot: BootResponse;
  products: Product[];
  maxBundlePreviewProducts: number;
  offerUIS: OfferUI[];
  promotionBadges?: OfferBadge[];
  tabs?: any[];
  navigate: NavigateFunction;
}): OfferAPIResponse => {
  const missingTypes = [
    OfferDesignTypes.Bundle,
    OfferDesignTypes.SpecialOffer,
    OfferDesignTypes.RollingOffer
  ].filter((type) => !offerUIS.some((ui) => ui.offerUiType === type));

  if (missingTypes.length > 0) {
    navigate('../');
    showToast({
      message:
        'Failed to load template. Missing offer UI of types: ' +
        missingTypes.join(', '),
      status: EToasterStatus.ERROR
    });
  }

  const lastProducts = products.slice(0, OFFER_PRODUCTS_LIMIT);

  const bundleProducts = products.slice(0, maxBundlePreviewProducts);

  const hasDailyBonusPopupOfferUI = offerUIS.some(
    (ui) =>
      ui.offerUiType === OfferDesignTypes.PopUp &&
      ui.offerUiSubType === EPopupSubType.DAILY_BONUS
  );

  const hasTriggerPopupOfferUI = offerUIS.some(
    (ui) =>
      ui.offerUiType === OfferDesignTypes.PopUp &&
      (ui.offerUiSubType === EPopupSubType.STORE_REFRESH ||
        ui.offerUiSubType === EPopupSubType.POST_PURCHASE)
  );

  const hasRewardCalendarPopupOfferUI = offerUIS.some(
    (ui) =>
      ui.offerUiType === OfferDesignTypes.PopUp &&
      ui.offerUiSubType === EPopupSubType.REWARD_CALENDAR
  );

  const finalOfferUIS = [
    ...offerUIS,
    ...(hasDailyBonusPopupOfferUI ? [] : [createDefaultPopupOfferUI()]),
    ...(hasTriggerPopupOfferUI ? [] : [createDefaultStoreRefreshPopupOfferUI()]),
    ...(hasRewardCalendarPopupOfferUI ? [] : [createDefaultRewardCalendarPopupOfferUI()])
  ];

  const promotions = generatePromotionOffers(
    formValues,
    products,
    finalOfferUIS,
    promotionBadges
  ).map((promotion) => createOffer(promotion, finalOfferUIS, boot));

  const popups = generatePopupsOffers(lastProducts, finalOfferUIS);
  const bundles = generateBundles(
    formValues,
    bundleProducts,
    finalOfferUIS
  ).map((bundle) => createOffer(bundle, finalOfferUIS, boot));
  const rollingOffers = generateRollingOffers(
    formValues,
    lastProducts,
    finalOfferUIS
  );

  const progressBars = generateProgressBars(
    formValues,
    lastProducts,
    finalOfferUIS
  );

  return {
    offers: {
      progressBars: progressBars,
      popups: [
        ...popups.map((popup) => createOffer(popup, finalOfferUIS, boot))
      ],
      promotions: promotions,
      bundles: bundles,
      rolling: [
        ...rollingOffers.map((rollingOffer) =>
          createOffer(rollingOffer, finalOfferUIS, boot)
        )
      ]
    },
    balances: generateBalances(products),
    offerFocus: {},
    profileFrameImg: '',
    playerLevelImg: '',
    publisherSettings: {},
    bannerImage: '',
    playerLevels: [],
    tabs: tabs,
    logo: ''
  };
};
